import { PARTIES } from '@val/shared'

/** En nämnd med sina ledamöter, ersättare, presidium och ev. utskott. */
export interface Committee {
  id: string
  name: string
  /** Antal ledamöter (tillsätts proportionellt). */
  seats: number
  /** Antal ersättare (tillsätts proportionellt, som ett eget val). */
  substitutes: number
  /**
   * Antal presidieposter (ordförande + vice ordförande). Väljs genom
   * majoritetsval bland de invalda ledamöterna – inte proportionellt – så det
   * är en delmängd av ledamotsplatserna ovan, inte extra platser.
   */
  presidium: number
  /** Fritext om nämndens utskott (arbetsutskott m.fl.), t.ex. "5 ledamöter". */
  au: string
}

const GROUPS_KEY = 'val.committeeGroups.v1'
const LIST_KEY = 'val.committees.v2'

/**
 * Dagens valtekniska samverkan i Ale KF. Partier som inte finns med i någon
 * grupp räknas som fristående (får då oftast inga nämndplatser alls).
 */
export const DEFAULT_GROUPS: string[][] = [
  ['M', 'SD', 'KD', 'FiA'],
  ['S', 'V', 'C'],
]

export const MAX_GROUPS = 4

/**
 * Ales nämnder enligt "Reglemente och arbetsordning för styrelse och nämnder
 * i Ale kommun" (beslutad av kommunfullmäktige 2026-02-09 § 21). Kultur- och
 * fritidsnämnden finns inte som egen nämnd idag (kultur- och fritidsfrågor
 * ligger i ett utskott under kommunstyrelsen) – den är med här som ett
 * hypotetiskt tillägg, redigera eller ta bort den fritt.
 */
export const DEFAULT_COMMITTEES: Committee[] = [
  {
    id: 'ks',
    name: 'Kommunstyrelsen',
    seats: 9,
    substitutes: 9,
    presidium: 3,
    au: '5 ledamöter (arbetsutskott, obligatoriskt) + personalutskott 5 ledamöter + kultur- och fritidsutskott 3 ledamöter',
  },
  {
    id: 'ubn',
    name: 'Utbildningsnämnden',
    seats: 9,
    substitutes: 9,
    presidium: 3,
    au: '3–5 ledamöter (frivilligt arbetsutskott)',
  },
  {
    id: 'son',
    name: 'Socialnämnden',
    seats: 9,
    substitutes: 9,
    presidium: 3,
    au: '3–5 ledamöter (frivilligt arbetsutskott) + individutskott 3 ledamöter (obligatoriskt)',
  },
  {
    id: 'sbn',
    name: 'Samhällsbyggnadsnämnden',
    seats: 9,
    substitutes: 9,
    presidium: 3,
    au: '3–5 ledamöter (frivilligt arbetsutskott)',
  },
  {
    id: 'sen',
    name: 'Servicenämnden',
    seats: 7,
    substitutes: 7,
    presidium: 3,
    au: '3–5 ledamöter (frivilligt arbetsutskott)',
  },
  {
    id: 'javn',
    name: 'Jävsnämnden',
    seats: 3,
    substitutes: 3,
    presidium: 2,
    au: 'Inget utskott',
  },
  {
    id: 'valn',
    name: 'Valnämnden',
    seats: 9,
    substitutes: 9,
    presidium: 3,
    au: 'Inget utskott',
  },
  {
    id: 'kfn',
    name: 'Kultur- och fritidsnämnden',
    seats: 7,
    substitutes: 7,
    presidium: 3,
    au: '(hypotetisk nämnd – finns inte idag)',
  },
]

const PARTY_CODES = new Set(PARTIES.map((p) => p.code))

function sanitizeGroups(raw: unknown): string[][] | null {
  if (!Array.isArray(raw)) return null
  const seen = new Set<string>()
  const groups: string[][] = []
  for (const g of raw.slice(0, MAX_GROUPS)) {
    if (!Array.isArray(g)) continue
    const members = g.filter(
      (c): c is string => typeof c === 'string' && PARTY_CODES.has(c) && !seen.has(c),
    )
    for (const c of members) seen.add(c)
    if (members.length > 0) groups.push(members)
  }
  return groups
}

export function getGroups(): string[][] {
  try {
    const parsed = sanitizeGroups(JSON.parse(localStorage.getItem(GROUPS_KEY) ?? 'null'))
    if (parsed) return parsed
  } catch {
    /* ignorera */
  }
  return DEFAULT_GROUPS.map((g) => [...g])
}

export function setGroups(groups: string[][]): void {
  try {
    localStorage.setItem(GROUPS_KEY, JSON.stringify(sanitizeGroups(groups) ?? []))
  } catch {
    /* ignorera */
  }
}

/** Grupp-index (0-baserat) per partikod. `-1` = fristående. */
export function groupIndexByParty(groups: string[][]): Record<string, number> {
  const out: Record<string, number> = {}
  for (const p of PARTIES) out[p.code] = -1
  groups.forEach((g, i) => {
    for (const c of g) out[c] = i
  })
  return out
}

/** Sätter (eller tar bort) ett partis grupptillhörighet och städar tomma grupper. */
export function assignParty(groups: string[][], code: string, groupIndex: number): string[][] {
  const next = groups.map((g) => g.filter((c) => c !== code))
  if (groupIndex >= 0) {
    while (next.length <= groupIndex) next.push([])
    next[groupIndex] = [...(next[groupIndex] ?? []), code]
  }
  return next.filter((g) => g.length > 0)
}

function clampCount(n: unknown, fallback: number, max = 101): number {
  return Math.min(max, Math.max(0, Math.round(Number(n) || fallback)))
}

function sanitizeCommittees(raw: unknown): Committee[] | null {
  if (!Array.isArray(raw)) return null
  const out: Committee[] = []
  for (const c of raw) {
    if (!c || typeof c !== 'object') continue
    const { id, name, seats, substitutes, presidium, au } = c as Record<string, unknown>
    if (typeof name !== 'string') continue
    const ledamoter = clampCount(seats, 1) || 1
    out.push({
      id: typeof id === 'string' ? id : `c_${out.length}`,
      name,
      seats: ledamoter,
      substitutes: clampCount(substitutes, ledamoter),
      presidium: clampCount(presidium, 3, 9),
      au: typeof au === 'string' ? au : '',
    })
  }
  return out
}

export function getCommittees(): Committee[] {
  try {
    const parsed = sanitizeCommittees(JSON.parse(localStorage.getItem(LIST_KEY) ?? 'null'))
    if (parsed) return parsed
  } catch {
    /* ignorera */
  }
  return DEFAULT_COMMITTEES.map((c) => ({ ...c }))
}

export function setCommittees(list: Committee[]): void {
  try {
    localStorage.setItem(LIST_KEY, JSON.stringify(sanitizeCommittees(list) ?? []))
  } catch {
    /* ignorera */
  }
}

export function newCommitteeId(): string {
  return `c_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e4).toString(36)}`
}

/**
 * I praktiken tar den styrande gruppen 2 av 3 presidieposter (formellt är
 * alla poster majoritetsval, öppna för vem som helst). Vilken av vice-posterna
 * som går till oppositionen har varierat i Ale:
 * - "historisk": 1:e vice ordförande till oppositionen (gällde fram tills nu)
 * - "ny": 2:e vice ordförande till oppositionen (nytt beslut – hotas rivas upp
 *   om en S-ledd styrning får majoritet)
 * - "allt": alla poster till den styrande gruppen
 */
export type PresidiumConvention = 'ny' | 'historisk' | 'allt'

export interface PresidiumSettings {
  /** Id på den grupp som antas styra (samma id-format som allocateCommittee). */
  styrandeGroupId: string | null
  convention: PresidiumConvention
}

const PRESIDIUM_KEY = 'val.committeePresidium.v1'

export function getPresidiumSettings(): PresidiumSettings {
  try {
    const raw = JSON.parse(localStorage.getItem(PRESIDIUM_KEY) ?? 'null')
    if (raw && typeof raw === 'object') {
      const convention: PresidiumConvention =
        raw.convention === 'historisk' || raw.convention === 'allt' ? raw.convention : 'ny'
      const styrandeGroupId = typeof raw.styrandeGroupId === 'string' ? raw.styrandeGroupId : null
      return { styrandeGroupId, convention }
    }
  } catch {
    /* ignorera */
  }
  return { styrandeGroupId: null, convention: 'ny' }
}

export function setPresidiumSettings(s: PresidiumSettings): void {
  try {
    localStorage.setItem(PRESIDIUM_KEY, JSON.stringify(s))
  } catch {
    /* ignorera */
  }
}

/** Vem som får vilken presidiepost, givet vald konvention. `null` = ingen post. */
export function presidiumSplit(
  totalPosts: number,
  convention: PresidiumConvention,
): { role: string; styrande: boolean }[] {
  if (totalPosts <= 0) return []
  if (totalPosts === 1) return [{ role: 'Ordförande', styrande: true }]
  if (totalPosts === 2) {
    return [
      { role: 'Ordförande', styrande: true },
      { role: '1:e vice ordförande', styrande: convention === 'allt' },
    ]
  }
  // 3 poster (eller fler – extra poster läggs till den styrande gruppen).
  const roles = ['Ordförande', '1:e vice ordförande', '2:e vice ordförande']
  for (let i = 3; i < totalPosts; i++) roles.push(`${i}:e vice ordförande`)
  return roles.map((role, i) => {
    if (i === 0 || convention === 'allt') return { role, styrande: true }
    if (i === 1) return { role, styrande: convention === 'ny' }
    if (i === 2) return { role, styrande: convention === 'historisk' }
    return { role, styrande: true }
  })
}
